// Runs the animated bits from the sky's frame loop: counts down to the next
// shooting star and satellite (only while they may happen, so a hidden tab
// or a word in progress never builds up a queue), keeps the day's share of
// visible stars twinkling, and clears everything under reduced motion.
import type { QuietZone } from '../skyMotionStore';
import { fieldToScreen } from '../drift';
import { FIELD_HEIGHT, FIELD_WIDTH, HALF_HEIGHT, type BackgroundStar } from '../weekdaySkies';
import type { AmbientConfig, AmbientDay } from './ambientConfig';
import {
  mayShootingStar,
  maySatellite,
  mayTwinkle,
  nextDelayS,
  pickSatellite,
  pickShootingStar,
  pointClear,
  twinkleTarget,
  type AmbientGate,
  type View,
} from './schedule';
import { spawnSatellite, spawnShootingStar, twinkleStar } from './spawn';

export type AmbientKind = 'shooting-star' | 'satellite' | 'twinkle';

export interface AmbientFrame {
  cfg: AmbientConfig;
  day: AmbientDay;
  gate: AmbientGate;
  view: View;
  /** The field y at the top of the view: 0 for play, 1000 for home. */
  halfTop: number;
  /** Each depth layer's offset, sky units. */
  offsets: readonly number[];
  stars: readonly BackgroundStar[];
  zones: readonly QuietZone[];
}

export class AmbientRuntime {
  private live = new Set<Animation>();
  private twinkling = new Set<Animation>();
  private twinklingStars = new Set<number>();
  private untilShootingStar = Number.NaN;
  private untilSatellite = Number.NaN;
  private untilTwinkleMs = 0;
  private last: AmbientFrame | null = null;

  constructor(
    private readonly layer: HTMLElement,
    private readonly starRoot: HTMLElement,
    private readonly rand: () => number = Math.random,
  ) {}

  tick(dtMs: number, f: AmbientFrame): void {
    this.last = f;
    const dt = dtMs / 1000;
    if (f.gate.reduced) {
      this.clear();
      return;
    }

    if (Number.isNaN(this.untilShootingStar)) this.untilShootingStar = nextDelayS(f.day.shootingStarEveryS, f.cfg.shootingStar.jitter, this.rand);
    if (mayShootingStar(f.gate)) {
      this.untilShootingStar -= dt;
      if (this.untilShootingStar <= 0) {
        this.shootingStar(f);
        this.untilShootingStar = nextDelayS(f.day.shootingStarEveryS, f.cfg.shootingStar.jitter, this.rand);
      }
    }

    if (f.day.satelliteEveryS === null) this.untilSatellite = Number.NaN;
    else {
      if (Number.isNaN(this.untilSatellite)) this.untilSatellite = nextDelayS(f.day.satelliteEveryS, f.cfg.satellite.jitter, this.rand);
      if (maySatellite(f.gate)) {
        this.untilSatellite -= dt;
        if (this.untilSatellite <= 0) {
          this.satellite(f);
          this.untilSatellite = nextDelayS(f.day.satelliteEveryS, f.cfg.satellite.jitter, this.rand);
        }
      }
    }

    if (mayTwinkle(f.gate)) {
      const inView = f.stars.length * Math.min(1, f.view.width / f.view.scale / FIELD_WIDTH) * (HALF_HEIGHT / FIELD_HEIGHT);
      const target = twinkleTarget(f.day.twinkleShare, inView);
      this.untilTwinkleMs -= dtMs;
      if (target > 0 && this.twinkling.size < target && this.untilTwinkleMs <= 0) {
        this.twinkle(f);
        // Starts spread out so about `target` overlap at any moment.
        const meanMs = ((f.cfg.twinkle.minS + f.cfg.twinkle.maxS) / 2) * 1000;
        this.untilTwinkleMs = (meanMs / target) * (0.5 + this.rand());
      }
    }
  }

  /** Fire one now, whatever the schedule says (the tuning panel's buttons). A twinkle fires on six bright stars at once, to be seen. */
  fire(kind: AmbientKind): void {
    if (!this.last || this.last.gate.reduced) return;
    if (kind === 'shooting-star') this.shootingStar(this.last);
    else if (kind === 'satellite') this.satellite(this.last);
    else for (let i = 0; i < 6; i++) this.twinkle(this.last, 2);
  }

  /** Forget the countdowns, e.g. when the day or its intervals change. */
  reschedule(): void {
    this.untilShootingStar = Number.NaN;
    this.untilSatellite = Number.NaN;
  }

  clear(): void {
    for (const a of [...this.live]) a.cancel();
  }

  private track(anim: Animation, twinkle = false): void {
    this.live.add(anim);
    if (twinkle) this.twinkling.add(anim);
    const done = () => {
      this.live.delete(anim);
      this.twinkling.delete(anim);
    };
    anim.addEventListener('finish', done);
    anim.addEventListener('cancel', done);
  }

  private shootingStar(f: AmbientFrame): void {
    const path = pickShootingStar(f.view, f.cfg.shootingStar, f.zones, this.rand);
    if (path) this.track(spawnShootingStar(this.layer, path, f.halfTop * f.view.scale, f.cfg.shootingStar, f.view.scale));
  }

  private satellite(f: AmbientFrame): void {
    const path = pickSatellite(f.view, f.zones, this.rand);
    if (path) this.track(spawnSatellite(this.layer, path, f.halfTop * f.view.scale, f.cfg.satellite));
  }

  /**
   * One twinkle on a star in view and outside the quiet zones. Twinkles
   * favour the brighter stars, where one can be seen: a far star is picked
   * about a sixth as often. minLayer 2 keeps to the near ones.
   */
  private twinkle(f: AmbientFrame, minLayer = 0): void {
    const inView = f.stars.filter((star) => {
      if (star.layer < minLayer || this.twinklingStars.has(star.id)) return false;
      const at = fieldToScreen(star.x, star.y, f.offsets[star.layer] ?? 0, f.halfTop, f.view, FIELD_WIDTH);
      return at !== null && pointClear(at, f.zones);
    });
    for (let tries = 0; tries < 12; tries++) {
      const star = inView[Math.floor(this.rand() * inView.length)];
      if (!star) return;
      if (star.layer === 0 && this.rand() < 0.85) continue;
      const els = this.starRoot.querySelectorAll(`[data-star="${star.id}"]`);
      if (els.length === 0) continue;
      const durationMs = (f.cfg.twinkle.minS + (f.cfg.twinkle.maxS - f.cfg.twinkle.minS) * this.rand()) * 1000;
      this.twinklingStars.add(star.id);
      els.forEach((el, i) => {
        const anim = twinkleStar(el, star.opacity, f.cfg.twinkle, durationMs);
        if (i === 0) {
          const done = () => this.twinklingStars.delete(star.id);
          anim.addEventListener('finish', done);
          anim.addEventListener('cancel', done);
        }
        this.track(anim, i === 0);
      });
      return;
    }
  }
}
